import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth-store';
import { api } from '@/lib/api';

interface PagePerformance {
  page: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

interface CannibalizationIssue {
  query: string;
  totalClicks: number;
  totalImpressions: number;
  avgPosition: number;
  pages: PagePerformance[];
  severity: 'high' | 'medium' | 'low';
  potentialClickLoss: number;
}

interface QueryPageData {
  query: string;
  page: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

/**
 * Detects SEO cannibalization - queries that rank with multiple pages
 * splitting traffic and potentially hurting overall performance
 */
export function useGSCCannibalization(minImpressions = 50) {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const siteUrl = activeProperty?.gscSite?.siteUrl;

  return useQuery({
    queryKey: ['gsc-cannibalization', siteUrl, minImpressions],
    queryFn: async (): Promise<CannibalizationIssue[] | null> => {
      if (!siteUrl) {
        return null;
      }

      try {
        // Get query + page dimension data from GSC
        const response = await api.google.runGSCQuery({
          siteUrl,
          startDate: getDateString(-28),
          endDate: getDateString(-1),
          dimensions: ['query', 'page'],
          rowLimit: 5000,
        });

        if (!response?.rows) {
          return [];
        }

        // Parse all query-page combinations
        const queryPageData: QueryPageData[] = response.rows.map((row: any) => ({
          query: row.keys?.[0] || '',
          page: row.keys?.[1] || '',
          clicks: row.clicks || 0,
          impressions: row.impressions || 0,
          ctr: (row.ctr || 0) * 100,
          position: row.position || 0,
        }));

        // Group by query
        const queryGroups = new Map<string, QueryPageData[]>();
        for (const item of queryPageData) {
          if (item.impressions < 10) continue; // Skip very low impression entries

          const existing = queryGroups.get(item.query) || [];
          existing.push(item);
          queryGroups.set(item.query, existing);
        }

        // Find cannibalization issues (queries with 2+ pages)
        const issues: CannibalizationIssue[] = [];

        for (const [query, pages] of queryGroups) {
          if (pages.length < 2) continue;

          const totalClicks = pages.reduce((sum, p) => sum + p.clicks, 0);
          const totalImpressions = pages.reduce((sum, p) => sum + p.impressions, 0);

          if (totalImpressions < minImpressions) continue;

          // Calculate weighted average position
          const avgPosition = pages.reduce((sum, p) => sum + p.position * p.impressions, 0) / totalImpressions;

          // Sort pages by clicks (best performing first)
          const sortedPages = [...pages].sort((a, b) => b.clicks - a.clicks);

          // Calculate potential click loss (estimate if we consolidated to best page)
          // Assume consolidating could improve position and CTR
          const bestPage = sortedPages[0];
          const otherPagesClicks = totalClicks - bestPage.clicks;
          // Estimate 40% of other page clicks could be captured by consolidation
          const potentialClickLoss = Math.round(otherPagesClicks * 0.4);

          // Determine severity
          let severity: CannibalizationIssue['severity'];
          if (pages.length >= 4 || potentialClickLoss > 50) {
            severity = 'high';
          } else if (pages.length >= 3 || potentialClickLoss > 20) {
            severity = 'medium';
          } else {
            severity = 'low';
          }

          issues.push({
            query,
            totalClicks,
            totalImpressions,
            avgPosition,
            pages: sortedPages.map(p => ({
              page: p.page,
              clicks: p.clicks,
              impressions: p.impressions,
              ctr: p.ctr,
              position: p.position,
            })),
            severity,
            potentialClickLoss,
          });
        }

        // Sort by severity then by total impressions
        const severityOrder = { high: 0, medium: 1, low: 2 };
        return issues.sort((a, b) => {
          const severityDiff = severityOrder[a.severity] - severityOrder[b.severity];
          if (severityDiff !== 0) return severityDiff;
          return b.totalImpressions - a.totalImpressions;
        });
      } catch (error) {
        console.error('Failed to fetch GSC cannibalization data:', error);
        return [];
      }
    },
    enabled: !!siteUrl,
    staleTime: 1000 * 60 * 15, // 15 minutes
  });
}

/**
 * Get summary statistics for cannibalization issues
 */
export function useCannibalizationSummary() {
  const { data: issues } = useGSCCannibalization();

  if (!issues) {
    return null;
  }

  const highSeverity = issues.filter(i => i.severity === 'high').length;
  const mediumSeverity = issues.filter(i => i.severity === 'medium').length;
  const lowSeverity = issues.filter(i => i.severity === 'low').length;
  const totalPotentialLoss = issues.reduce((sum, i) => sum + i.potentialClickLoss, 0);
  const avgPagesPerQuery = issues.length > 0
    ? issues.reduce((sum, i) => sum + i.pages.length, 0) / issues.length
    : 0;

  return {
    totalIssues: issues.length,
    highSeverity,
    mediumSeverity,
    lowSeverity,
    totalPotentialLoss,
    avgPagesPerQuery: avgPagesPerQuery.toFixed(1),
  };
}

function getDateString(daysOffset: number): string {
  const date = new Date();
  date.setDate(date.getDate() + daysOffset);
  return date.toISOString().split('T')[0];
}
